import { Add as AddIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material'
import { Typography, MenuItem, Stack, Button, Alert } from '@mui/material'
import type { FC } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { useForm, useWatch } from 'react-hook-form'
import { Link } from 'react-router-dom'

import MuiSelectField from '@/components/Forms/MuiSelectField'
import FormStack from '@/components/UI/FormContainer'
import PaperedGridItem from '@/components/UI/PaperedGridItem'
import useUser from '@/hooks/useUser'
import { addDemoApplicantMutation, toErrorNode } from '@/lib/apollo'
import type {
  DemoApplicantType,
  DemoGuarantorProduct,
  DemoIdvtType,
} from '@/lib/apollo/mutation/addDemoApplicant/mutation'

type DemoIdentityProviderType = 'entrust' | 'default'

type GuarantorCollectionOption = 'yes' | 'no'

type Inputs = {
  applicantType: DemoApplicantType
  identityProvider: DemoIdentityProviderType
  idvt: DemoIdvtType
  guarantorCollection: GuarantorCollectionOption
  guarantorProduct: DemoGuarantorProduct
}

// Guarantor applicant types create a guarantor subject, so tenant-only
// options (IDVT and guarantor collection) do not apply to them.
const guarantorApplicantTypes: DemoApplicantType[] = [
  'advancedGuarantor',
  'selfServeGuarantor',
]

const guarantorProducts: { value: DemoGuarantorProduct; label: string }[] = [
  { value: 'Essential', label: 'Essential' },
  { value: 'Advanced', label: 'Advanced' },
  { value: 'Pro', label: 'Pro' },
  { value: 'Priority', label: 'Priority' },
  { value: 'Corporate', label: 'Corporate' },
  { value: 'DataCollection', label: 'Data Collection' },
  { value: 'SelfServe', label: 'Self Serve' },
]

const AddDemoApplicantTab: FC = () => {
  const { handleSubmit, control } = useForm<Inputs>({
    defaultValues: {
      applicantType: 'essentialTenant',
      identityProvider: 'default',
      idvt: 'NotRequired',
      guarantorCollection: 'no',
      guarantorProduct: 'Pro',
    },
  })

  const [applicantType, idvt, guarantorCollection] = useWatch({
    control,
    name: ['applicantType', 'idvt', 'guarantorCollection'],
  })

  const isTenantType = !guarantorApplicantTypes.includes(applicantType)
  const idvtRequired = isTenantType && idvt !== 'NotRequired'
  const guarantorCollectionRequired = isTenantType && guarantorCollection === 'yes'

  const { customerId, userEmail } = useUser()

  const [handleAddDemoApplicant, { loading, error, data }] =
    addDemoApplicantMutation.useMutation()

  const onSubmit: SubmitHandler<Inputs> = data =>
    handleAddDemoApplicant({
      variables: {
        email: userEmail,
        customerId,
        applicantType: data.applicantType,
        // Credas is deprecated, so any IDVT requirement goes through Entrust
        identityProvider: idvtRequired ? 'entrust' : data.identityProvider,
        ...(isTenantType && {
          idvt: data.idvt,
          guarantorCollectionRequired,
          ...(guarantorCollectionRequired && {
            guarantorProduct: data.guarantorProduct,
          }),
        }),
      },
    })

  return (
    <PaperedGridItem xs={12}>
      {data !== null && data !== undefined ? (
        <Stack spacing={2}>
          <Typography>Your demo applicant has been created.</Typography>
          <Button
            component={Link}
            to={`/applicant/${data?.addDemoApplicant.subjectId}`}
            variant="contained"
            sx={{ width: 200 }}
            endIcon={<ArrowForwardIcon />}
          >
            View applicant
          </Button>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Typography component="h2" variant="h6">
              Create an applicant for demo purposes
            </Typography>
            <FormStack width={600}>
              <MuiSelectField
                id="applicantType"
                name="applicantType"
                label="Applicant Type"
                control={control}
              >
                <MenuItem value="essentialTenant">Essential tenant</MenuItem>
                <MenuItem value="advancedTenant">Pro tenant</MenuItem>
                <MenuItem value="advancedGuarantor">Pro guarantor</MenuItem>
                <MenuItem value="selfServeTenant">Self Serve tenant</MenuItem>
                <MenuItem value="selfServeGuarantor">Self Serve guarantor</MenuItem>
                <MenuItem value="selfServeStudentTenant">
                  Self Serve Student tenant
                </MenuItem>
              </MuiSelectField>
              <MuiSelectField
                id="identityProvider"
                name="identityProvider"
                label="Identity Provider"
                control={control}
                describedBy={idvtRequired ? 'identityProviderNote' : undefined}
              >
                <MenuItem value="default">Default</MenuItem>
                <MenuItem value="entrust">Entrust</MenuItem>
              </MuiSelectField>
              {isTenantType && (
                <>
                  <MuiSelectField id="idvt" name="idvt" label="IDVT" control={control}>
                    <MenuItem value="NotRequired">Not required</MenuItem>
                    <MenuItem value="UKOnly">UK only</MenuItem>
                    <MenuItem value="Required">Required</MenuItem>
                  </MuiSelectField>
                  {idvtRequired && (
                    <Typography
                      id="identityProviderNote"
                      variant="caption"
                      color="text.secondary"
                    >
                      IDVT is provided by Entrust, so the identity provider will be set to
                      Entrust.
                    </Typography>
                  )}
                  <MuiSelectField
                    id="guarantorCollection"
                    name="guarantorCollection"
                    label="Guarantor Collection"
                    control={control}
                  >
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </MuiSelectField>
                  {guarantorCollectionRequired && (
                    <MuiSelectField
                      id="guarantorProduct"
                      name="guarantorProduct"
                      label="Guarantor Product"
                      control={control}
                    >
                      {guarantorProducts.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </MuiSelectField>
                  )}
                </>
              )}
              <Stack alignItems="end">
                <Button
                  type="submit"
                  name="create"
                  variant="contained"
                  loading={loading}
                  sx={{ width: 200 }}
                  loadingPosition="end"
                  endIcon={<AddIcon />}
                >
                  Create
                </Button>
              </Stack>
              {error && <Alert severity="error">{toErrorNode(error)}</Alert>}
            </FormStack>
          </Stack>
        </form>
      )}
    </PaperedGridItem>
  )
}

export default AddDemoApplicantTab
